import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { StructuredFieldProps } from "./projectDetailFields";
import {
  CitySuggestion,
  EMPTY_LOCATION_DETAILS,
  MIN_QUERY_LENGTH,
  detailsFromSuggestion,
  placesEnabled,
  searchCities,
} from "../../../libs/places";

// Matches the library search's text debounce.
const DEBOUNCE_MS = 300;

const LISTBOX_PROPS = { sx: { fontSize: "0.8rem" } };
const NO_FILTER = (options: CitySuggestion[]) => options;

/**
 * Free-solo city autocomplete for the project Location (#3787). A picked
 * suggestion stores the structured city; typed text is still accepted and
 * clears any stored structure. With no Places key it's a plain text input —
 * the intended local-dev and CI mode.
 */
export const ProjectLocationField = ({
  label,
  id,
  project,
  onChange,
  floatingLabel = false,
}: StructuredFieldProps) => {
  const [options, setOptions] = useState<CitySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const value = project.location || "";
  // Debounced fetches can resolve out of order — only the newest request may
  // write state.
  const requestId = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      // Blocks any in-flight response from setting state after unmount.
      requestId.current += 1;
    },
    [],
  );

  const runSearch = (input: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!placesEnabled()) return;
    // Skip the debounce/spinner for queries searchCities would reject anyway.
    if (input.trim().length < MIN_QUERY_LENGTH) {
      requestId.current += 1; // supersede any in-flight response
      setOptions([]);
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    timer.current = setTimeout(async () => {
      const results = await searchCities(input);
      if (id !== requestId.current) return; // superseded
      setOptions(results);
      setLoading(false);
    }, DEBOUNCE_MS);
  };

  return (
    <Autocomplete<CitySuggestion, false, false, true>
      freeSolo
      fullWidth
      id={id}
      size="small"
      options={options}
      loading={loading}
      // Already matched server-side; MUI must not re-filter against raw input.
      filterOptions={NO_FILTER}
      value={value}
      inputValue={value}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      noOptionsText="No matches"
      onChange={(_, selected) => {
        if (!selected || typeof selected === "string") return;
        requestId.current += 1; // stop any in-flight list from reopening
        // The superseded response can no longer clear the spinner — do it here.
        if (timer.current) clearTimeout(timer.current);
        setLoading(false);
        setOptions([]);
        onChange({
          location: selected.description,
          locationDetails: detailsFromSuggestion(selected),
        });
      }}
      onInputChange={(_, input, reason) => {
        if (reason !== "input") return; // "reset" fires from the select above
        // Typed text supersedes stored structure. All four keys written, never
        // a partial: saves deep-merge, and a partial would leave the old
        // city's region/placeId behind. Gated on placeId so the clear fires
        // once, not on every keystroke after it.
        onChange({
          location: input,
          ...(project.locationDetails?.placeId
            ? { locationDetails: EMPTY_LOCATION_DETAILS }
            : {}),
        });
        runSearch(input);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={floatingLabel ? label : undefined}
          placeholder="Start typing a city…"
          // Spread, don't replace: params.inputProps carries the widget's ref/aria.
          inputProps={{ ...params.inputProps, "aria-label": label }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={LISTBOX_PROPS}
    />
  );
};
